import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Send, Eye } from 'lucide-react';

/**
 * Daily Settlement Summary card — preview the last-24h digest and send it now
 * to all admins. An automated version fires at 06:00 UTC via the backend scheduler.
 */
const SettlementSummaryCard = ({ api }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [delivery, setDelivery] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/settlement-summary/preview?hours=24');
      setData(res.data);
      setDelivery(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/settlement-summary/send-now?hours=24');
      setDelivery(res.data);
      const anySent = (res.data.delivery || []).some((d) => d.email_sent);
      if (anySent) toast.success('Summary emailed to admins');
      else toast.warning('Summary generated — email not delivered (check SendGrid credits)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send summary');
    } finally {
      setSending(false);
    }
  };

  const summary = data?.summary;

  return (
    <Card className="p-6" data-testid="settlement-summary-card">
      <div className="flex items-center gap-3 mb-3">
        <CalendarClock className="w-5 h-5 text-indigo-600" />
        <h3 className="text-xl font-semibold text-slate-800">Daily Settlement Summary</h3>
        <Badge variant="outline" className="text-slate-600 border-slate-300">Auto-emails 06:00 UTC</Badge>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={loading} data-testid="settlement-summary-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Preview (24h)
          </Button>
          <Button size="sm" onClick={sendNow} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="settlement-summary-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send now
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        A digest of every settlement recorded in the last 24 hours, emailed to all admin / super-admin accounts.
      </p>

      {data?.recipients && (
        <p className="text-[11px] text-slate-500 mb-3" data-testid="settlement-summary-recipients">
          Recipients: {data.recipients.join(', ') || '— none —'}
        </p>
      )}

      {summary && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-right px-3 py-2">Count</th>
                <th className="text-right px-3 py-2">Volume</th>
              </tr>
            </thead>
            <tbody>
              {summary.sources.map((s) => (
                <tr key={s.collection} className="border-b border-slate-100">
                  <td className="px-3 py-2">{s.label}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.count}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {Object.keys(s.by_currency || {}).length
                      ? Object.entries(s.by_currency).map(([k, v]) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${k}`).join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold bg-slate-50">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right font-mono" data-testid="settlement-summary-total-count">{summary.totals.count}</td>
                <td className="px-3 py-2 text-right font-mono">{Number(summary.totals.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {delivery && (
        <div className="mt-3 text-[11px] text-slate-600" data-testid="settlement-summary-delivery">
          {(delivery.delivery || []).map((d) => (
            <span key={d.email} className={`inline-block mr-2 mb-1 px-2 py-0.5 rounded border ${d.email_sent ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
              {d.email}: {d.email_sent ? 'sent' : 'not delivered'}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
};

export default SettlementSummaryCard;
